import * as React from 'react';

/** Matches ProgramCurriculumCollections `referenceCollectionId` union (preset vs org reference). */
export type ReferenceCollectionIdMember =
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_presetCollectionIdMember';
      presetCollectionId?: { id?: string | null } | null;
    }
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_orgCollectionIdMember';
      orgCollectionId?: { id?: string | null } | null;
    };

export type ReferenceCollectionConfig = {
  enableSync?: boolean | null;
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/** Shape needed to read a preset reference id (shared by context + collection fragments). */
export type ReferenceCollectionConfigurationLike = {
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/** Preset library id when the reference is a preset collection; undefined for org-backed references. */
export const getPresetReferenceCollectionId = (
  config: ReferenceCollectionConfigurationLike | undefined
): string | undefined => {
  const ref = config?.referenceCollectionId;
  if (ref && 'presetCollectionId' in ref) {
    return ref.presetCollectionId?.id ?? undefined;
  }
  return undefined;
};

type ReferenceCollectionConfigContextType = {
  /** Map of collection ID to reference collection configuration from ProgramCurriculumCollections. */
  referenceCollectionConfigById: Record<string, ReferenceCollectionConfig>;
};

const ReferenceCollectionConfigContext = React.createContext<ReferenceCollectionConfigContextType>({
  referenceCollectionConfigById: {},
});

export const useReferenceCollectionConfig = () => React.useContext(ReferenceCollectionConfigContext);

/**
 * Returns true if the collection is synced to a preset (reordered) collection.
 * Use with collection IDs from PageConfig (Collection_Collection.id) matched to ProgramCurriculumCollections.
 */
export const useIsPartOfReorderedCollection = (collectionId: string | undefined): boolean => {
  const { referenceCollectionConfigById } = useReferenceCollectionConfig();
  if (!collectionId) return false;
  const config = referenceCollectionConfigById[collectionId];
  return config?.enableSync === true && Boolean(getPresetReferenceCollectionId(config));
};

export default ReferenceCollectionConfigContext;
